import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  getSchemaSize,
  initVersionedSchema,
  MixedSchema,
} from '@principle-theorem/editor';
import {
  createInsertTemplateMenuButton,
  EditorPresetsService,
  PrincipleEditorModule,
} from '@principle-theorem/ng-interactions';
import {
  DismissibleInfoBoxComponent,
  DismissibleInfoBoxKey,
  OrganisationService,
} from '@principle-theorem/ng-principle-shared';
import { createInsertPatientDocumentMenuButton } from '@principle-theorem/ng-patient';
import {
  DialogPresets,
  NgCommonModule,
  NgFireMediaUploader,
  NG_SHARED_CONFIG,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import {
  Practice,
  SystemTemplates,
} from '@principle-theorem/principle-core';
import {
  IClinicalNote,
  IDocumentSettings,
  IPatient,
  IPractice,
  IReferral,
  IReferralClinicalNote,
  IReferralMedia,
  IReferralPatientLetter,
  ITemplateDefinitionWithName,
  ReferralActor,
  SystemTemplate,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  isSameRef,
  shareReplayCold,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { ReferralPatientLetterSidebarComponent } from '../referral-patient-letter-sidebar/referral-patient-letter-sidebar.component';
import {
  ReferralPatientLetterService,
  ISendPatientLetterOptions,
} from '../../services/referral-patient-letter.service';
import { ReferralStore } from '../../stores/referral.store';
import { from, Observable, Subject } from 'rxjs';
import { ReferralMediaSelectionComponent } from '../referral-media-selection/referral-media-selection.component';
import {
  ISendPatientLetterDialogData,
  ISendPatientLetterDialogResult,
  SendPatientLetterDialogComponent,
} from '../send-patient-letter-dialog/send-patient-letter-dialog.component';
import { Storage } from '@angular/fire/storage';
import { MenuButtonLoaderFn } from '@principle-theorem/ng-prosemirror';
import { buildReferralTemplatesWithContext } from '../../helpers/build-referral-templates-with-context';
import { resolvePatientLetterDefaults } from '../../helpers/resolve-patient-letter-defaults';
import { ReferralClinicalNoteSelectionComponent } from '../referral-clinical-note-selection/referral-clinical-note-selection.component';

interface IPatientLetterForm {
  content: MixedSchema;
}

@Component({
  selector: 'pr-referral-patient-letter',
  imports: [
    NgCommonModule,
    MatButtonModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatTabsModule,
    MatTooltipModule,
    PrincipleEditorModule,
    DismissibleInfoBoxComponent,
    ReferralPatientLetterSidebarComponent,
    ReferralMediaSelectionComponent,
    ReferralClinicalNoteSelectionComponent,
  ],
  templateUrl: './referral-patient-letter.component.html',
  styleUrl: './referral-patient-letter.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ReferralPatientLetterService],
})
export class ReferralPatientLetterComponent {
  private _store = inject(ReferralStore);
  private _organisation = inject(OrganisationService);
  private _dialog = inject(MatDialog);
  private _destroyRef = inject(DestroyRef);
  private _editorPresets = inject(EditorPresetsService);
  private _storage = inject(Storage);
  private _sharedConfig = inject(NG_SHARED_CONFIG);
  private _isSetupComplete = false;
  private _patientClinicalNotesLoaded = false;
  public letterService = inject(ReferralPatientLetterService);

  readonly DismissibleInfoBoxKey = DismissibleInfoBoxKey;

  referral = input.required<WithRef<IReferral>>();
  patient = input.required<WithRef<IPatient>>();
  practice = input.required<WithRef<IPractice>>();
  currentUser = input.required<ReferralActor>();
  media = input<WithRef<IReferralMedia>[]>([]);
  clinicalNotes = input<WithRef<IReferralClinicalNote>[]>([]);
  patientClinicalNotes = input<WithRef<IClinicalNote>[]>([]);

  menuItems = signal<MenuButtonLoaderFn[]>([]);

  headerTemplate = signal<MixedSchema | undefined>(undefined);
  footerTemplate = signal<MixedSchema | undefined>(undefined);
  selectedAttachmentRefs = signal<DocumentReference<IReferralMedia>[]>([]);
  selectedClinicalNoteRefs = signal<DocumentReference<IClinicalNote>[]>([]);
  includeClinicalNotesToggle = signal(false);

  form = new TypedFormGroup<IPatientLetterForm>({
    content: new TypedFormControl<MixedSchema>(initVersionedSchema()),
  });

  printTemplates$: Observable<ITemplateDefinitionWithName[]>;
  documentSettings$: Observable<IDocumentSettings>;

  hasContent = computed(() => {
    const content = this.form.controls.content.value;
    return content && getSchemaSize(content) > 0;
  });

  constructor() {
    const uploader = new NgFireMediaUploader(
      this._storage,
      this._organisation.storagePath$.pipe(filterUndefined())
    );

    this.documentSettings$ = this._organisation.practice$.pipe(
      map((practice) => Practice.resolveDocumentSettings(practice))
    );

    effect(() => {
      const patient = this.patient();
      const practice = this.practice();
      const referral = this.referral();
      const currentUser = this.currentUser();

      if (!this._isSetupComplete && referral && patient && practice) {
        this._isSetupComplete = true;
        this._initialiseContent(referral);
        this._patientClinicalNotesLoaded = true;
        this._store.loadPatientClinicalNotes(patient);
      }

      if (patient && practice && referral && currentUser) {
        const menuCoordinator$ = new Subject<string>();
        this.menuItems.set(
          this._editorPresets.defaultMenuItems(uploader, [
            createInsertTemplateMenuButton({
              templates$: buildReferralTemplatesWithContext(
                referral,
                practice,
                this._sharedConfig.appUrl,
                this._organisation.brand$.pipe(filterUndefined())
              ),
              menuCoordinator$,
            }),
            createInsertPatientDocumentMenuButton({
              patient,
              menuCoordinator$,
            }),
          ])
        );
      }
    });

    this._setupAutoSave();

    this.printTemplates$ = this._organisation.brand$.pipe(
      filterUndefined(),
      switchMap((brand) =>
        from(
          SystemTemplates.resolveSystemTemplates(brand, [
            SystemTemplate.PrintHeader,
            SystemTemplate.PrintFooter,
          ])
        )
      ),
      shareReplayCold()
    );
  }

  onHeaderTemplateChange(template: MixedSchema | undefined): void {
    this.headerTemplate.set(template);
    this._updateStoreLetter({ headerTemplate: template });
  }

  onFooterTemplateChange(template: MixedSchema | undefined): void {
    this.footerTemplate.set(template);
    this._updateStoreLetter({ footerTemplate: template });
  }

  async onPreview(): Promise<void> {
    const letter = this._buildCurrentLetter();
    if (!letter) {
      return;
    }
    const selectedNotes = this._resolveSelectedClinicalNotes();
    await this.letterService.previewPdf(letter, selectedNotes);
  }

  async onDownload(): Promise<void> {
    const letter = this._buildCurrentLetter();
    if (!letter) {
      return;
    }
    const selectedNotes = this._resolveSelectedClinicalNotes();
    const selectedMedia = this._resolveSelectedAttachments();
    await this.letterService.downloadPdf(
      letter,
      this.patient().name,
      selectedNotes,
      selectedMedia
    );
  }

  onAttachmentsChange(refs: DocumentReference<IReferralMedia>[]): void {
    this.selectedAttachmentRefs.set(refs);
    this._updateStoreLetter({ selectedAttachmentRefs: refs });
  }

  onClinicalNoteSelectionChange(
    refs: DocumentReference<IClinicalNote>[]
  ): void {
    this.selectedClinicalNoteRefs.set(refs);
    this._updateStoreLetter({ selectedClinicalNoteRefs: refs });
  }

  onClinicalNotesToggleChange(enabled: boolean): void {
    this.includeClinicalNotesToggle.set(enabled);
    this._updateStoreLetter({ includeClinicalNotes: enabled });
  }

  onTabChange(event: MatTabChangeEvent): void {
    if (event.index === 1 && !this._patientClinicalNotesLoaded) {
      this._patientClinicalNotesLoaded = true;
      this._store.loadPatientClinicalNotes(this.patient());
    }
  }

  async onSend(): Promise<void> {
    const patient = this.patient();
    const practice = this.practice();
    const currentUser = this.currentUser();

    const { defaultSubject, defaultEmailBody } =
      await this._resolvePatientLetterDefaults(patient, practice);

    const dialogData: ISendPatientLetterDialogData = {
      patient,
      practice,
      currentUser,
      referral: this.referral(),
      defaultSubject,
      defaultEmailBody,
    };

    const result = await this._dialog
      .open<
        SendPatientLetterDialogComponent,
        ISendPatientLetterDialogData,
        ISendPatientLetterDialogResult
      >(
        SendPatientLetterDialogComponent,
        DialogPresets.medium({
          data: dialogData,
        })
      )
      .afterClosed()
      .toPromise();

    if (!result) {
      return;
    }
    await this._sendLetter(result);
  }

  private async _sendLetter(
    dialogResult: ISendPatientLetterDialogResult
  ): Promise<void> {
    const brand = await snapshot(this._organisation.brand$);
    if (!brand) {
      return;
    }

    const formData = this.form.getRawValue();
    const includeClinicalNotes = this.includeClinicalNotesToggle();
    const selectedRefs = this.selectedClinicalNoteRefs();
    const selectedNotes = includeClinicalNotes
      ? this.patientClinicalNotes().filter((note) =>
          selectedRefs.some((ref) => isSameRef(ref, note.ref))
        )
      : [];

    const options: ISendPatientLetterOptions = {
      brandRef: brand.ref,
      referral: this.referral(),
      patient: this.patient(),
      currentUser: this.currentUser(),
      subject: dialogResult.subject,
      bodyContent: formData.content ?? initVersionedSchema(),
      emailBody: dialogResult.emailBody,
      headerTemplate: this.headerTemplate(),
      footerTemplate: this.footerTemplate(),
      attachmentRefs: this.selectedAttachmentRefs(),
      includeClinicalNotes,
      clinicalNotes: selectedNotes,
    };

    await this.letterService.sendPatientLetter(options);
  }

  private _initialiseContent(referral: WithRef<IReferral>): void {
    const existingLetter = referral.patientLetter;

    if (existingLetter?.content && getSchemaSize(existingLetter.content) > 0) {
      this.form.controls.content.setValue(existingLetter.content);

      if (existingLetter.headerTemplate) {
        this.headerTemplate.set(existingLetter.headerTemplate);
      }
      if (existingLetter.footerTemplate) {
        this.footerTemplate.set(existingLetter.footerTemplate);
      }
      if (existingLetter.selectedAttachmentRefs) {
        this.selectedAttachmentRefs.set(existingLetter.selectedAttachmentRefs);
      }
      if (existingLetter.selectedClinicalNoteRefs) {
        this.selectedClinicalNoteRefs.set(
          existingLetter.selectedClinicalNoteRefs
        );
      }
      if (existingLetter.includeClinicalNotes) {
        this.includeClinicalNotesToggle.set(
          existingLetter.includeClinicalNotes
        );
      }
      return;
    }
    this.form.controls.content.setValue(initVersionedSchema());
  }

  private _setupAutoSave(): void {
    this.form.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this._destroyRef))
      .subscribe((value) => {
        this._updateStoreLetter({
          content: value.content ?? initVersionedSchema(),
        });
      });
  }

  private _updateStoreLetter(updates: Partial<IReferralPatientLetter>): void {
    this._store.updatePatientLetter(updates);
  }

  private _resolveSelectedClinicalNotes(): WithRef<IClinicalNote>[] {
    const includeClinicalNotes = this.includeClinicalNotesToggle();
    if (!includeClinicalNotes) {
      return [];
    }
    const selectedRefs = this.selectedClinicalNoteRefs();
    return this.patientClinicalNotes().filter((note) =>
      selectedRefs.some((ref) => isSameRef(ref, note.ref))
    );
  }

  private _resolveSelectedAttachments(): WithRef<IReferralMedia>[] {
    const selectedRefs = this.selectedAttachmentRefs();
    return this.media().filter((mediaItem) =>
      selectedRefs.some((ref) => isSameRef(ref, mediaItem.ref))
    );
  }

  private _buildCurrentLetter(): IReferralPatientLetter | undefined {
    const formData = this.form.getRawValue();
    if (!formData.content) {
      return undefined;
    }

    return {
      content: formData.content,
      headerTemplate: this.headerTemplate(),
      footerTemplate: this.footerTemplate(),
      selectedAttachmentRefs: this.selectedAttachmentRefs(),
      selectedClinicalNoteRefs: this.selectedClinicalNoteRefs(),
      includeClinicalNotes: this.includeClinicalNotesToggle(),
    };
  }

  private async _resolvePatientLetterDefaults(
    patient: WithRef<IPatient>,
    practice: WithRef<IPractice>
  ): Promise<{ defaultSubject: string; defaultEmailBody: MixedSchema }> {
    const fallbackSubject = this.letterService.buildDefaultSubject(
      practice.name
    );
    const fallbackEmailBody = this.letterService.buildDefaultEmailBody(
      patient.name,
      practice.name
    );

    try {
      const { resolvedSubject, resolvedEmailBody } =
        await resolvePatientLetterDefaults(
          this.referral(),
          practice,
          this._sharedConfig.appUrl
        );

      return {
        defaultSubject: resolvedSubject ?? fallbackSubject,
        defaultEmailBody: resolvedEmailBody ?? fallbackEmailBody,
      };
    } catch {
      return {
        defaultSubject: fallbackSubject,
        defaultEmailBody: fallbackEmailBody,
      };
    }
  }
}
